import React, { useState, useEffect, useCallback, useMemo } from 'react';
import SiteLayout from '../../layouts/SiteLayout';
import Header from '../../components/Header/Header';
import Box from '../../components/Common/Box';
import { useAuth } from '../../context/AuthContext';
import Loader from '../../components/Common/Loader';
import { useScreenType } from '../../hooks/useScreenType';
import { API_URL } from '../../config';

interface Commission {
    id: number;
    created_at: string;
    referrer: { full_name: string };
    referred_user: { full_name: string };
    amount: number;
    status: 'PENDING' | 'VALIDATED' | 'PAID' | 'CANCELLED';
}

const AdminCommissionsScreen: React.FC = () => {
    const { token } = useAuth();
    const { isMobile } = useScreenType();
    const [commissions, setCommissions] = useState<Commission[]>([]);
    const [loading, setLoading] = useState(true);
    const [processingId, setProcessingId] = useState<number | null>(null);
    const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

    const fetchCommissions = useCallback(async () => {
        setLoading(true);
        try {
            const response = await fetch(`${API_URL}/v1/admin/commissions`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            if (response.ok) {
                const data = await response.json();
                setCommissions(data);
            } else {
                console.error('Failed to fetch commissions');
            }
        } catch (error) {
            console.error('Error fetching commissions:', error);
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        fetchCommissions();
    }, [fetchCommissions]);

    const handleMarkAsPaid = async (id: number) => {
        if (!window.confirm('Voulez-vous vraiment marquer cette commission comme payée ?')) return;
        
        setProcessingId(id);
        setMessage(null);
        try {
            const response = await fetch(`${API_URL}/v1/admin/commissions/${id}/pay`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                setMessage({ type: 'success', text: 'Commission marquée comme payée.' });
                // Update local state
                setCommissions(prev => prev.map(c => c.id === id ? { ...c, status: 'PAID' } : c));
            } else {
                const err = await response.json();
                setMessage({ type: 'error', text: err.detail || 'Erreur lors de la mise à jour.' });
            }
        } catch (error) {
            setMessage({ type: 'error', text: 'Erreur de connexion.' });
        } finally {
            setProcessingId(null);
        }
    };

    const summary = useMemo(() => {
        const toPay = commissions
            .filter(c => c.status === 'VALIDATED')
            .reduce((sum, c) => sum + c.amount, 0);
        
        const paid = commissions
            .filter(c => c.status === 'PAID')
            .reduce((sum, c) => sum + c.amount, 0);

        return { toPay, paid };
    }, [commissions]);

    const getStatusStyle = (status: string) => {
        switch (status) {
            case 'PAID':
                return { background: '#dcfce7', color: '#166534' };
            case 'VALIDATED':
                return { background: '#eff6ff', color: '#1d4ed8' };
            case 'PENDING':
                return { background: '#fef9c3', color: '#854d0e' };
            case 'CANCELLED':
                return { background: '#fee2e2', color: '#991b1b' };
            default:
                return { background: '#f1f5f9', color: '#475569' };
        }
    };

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XOF', maximumFractionDigits: 0 }).format(amount);
    };

    return (
        <SiteLayout>
            <Header icon='payments' title='Commissions Partenaires' />

            <div style={{ 
                padding: isMobile ? '10px' : '20px', 
                maxWidth: '1200px', 
                margin: '0 auto' 
            }}>
                
                {/* Summary Cards */}
                <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', 
                    gap: '20px', 
                    marginBottom: '20px' 
                }}>
                    <Box>
                        <div style={{ padding: '20px', textAlign: 'center' }}>
                            <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '5px' }}>Total à payer (VALIDATED)</div>
                            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#2563eb' }}>
                                {formatCurrency(summary.toPay)}
                            </div>
                        </div>
                    </Box>
                    <Box>
                        <div style={{ padding: '20px', textAlign: 'center' }}>
                            <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '5px' }}>Total payé (PAID)</div>
                            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#059669' }}>
                                {formatCurrency(summary.paid)}
                            </div>
                        </div>
                    </Box>
                </div>

                {message && (
                    <div style={{
                        padding: '15px',
                        marginBottom: '20px',
                        borderRadius: '5px',
                        background: message.type === 'success' ? '#dcfce7' : '#fee2e2',
                        color: message.type === 'success' ? '#166534' : '#991b1b',
                    }}>
                        {message.text}
                    </div>
                )}

                <Box>
                    <div style={{ padding: '20px', overflowX: 'auto' }}>
                        <h3 style={{ marginBottom: '20px' }}>Historique des Commissions</h3>

                        {loading ? (
                            <Loader />
                        ) : commissions.length === 0 ? (
                            <p style={{ color: '#64748b', textAlign: 'center', padding: '20px' }}>Aucune commission trouvée.</p>
                        ) : (
                            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '600px' }}>
                                <thead>
                                    <tr style={{ textAlign: 'left', borderBottom: '2px solid #e2e8f0' }}>
                                        <th style={{ padding: '12px' }}>Date</th>
                                        <th style={{ padding: '12px' }}>Parrain</th>
                                        <th style={{ padding: '12px' }}>Filleul</th>
                                        <th style={{ padding: '12px' }}>Montant</th>
                                        <th style={{ padding: '12px' }}>Statut</th>
                                        <th style={{ padding: '12px' }}>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {commissions
                                        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
                                        .map((comm) => (
                                            <tr key={comm.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                                <td style={{ padding: '12px', fontSize: '13px' }}>
                                                    {new Date(comm.created_at).toLocaleDateString('fr-FR')}
                                                </td>
                                                <td style={{ padding: '12px' }}>
                                                    <div style={{ fontWeight: '500' }}>{comm.referrer?.full_name}</div>
                                                </td>
                                                <td style={{ padding: '12px' }}>
                                                    <div style={{ fontSize: '13px' }}>{comm.referred_user?.full_name}</div>
                                                </td>
                                                <td style={{ padding: '12px', fontWeight: 'bold' }}>
                                                    {formatCurrency(comm.amount)}
                                                </td>
                                                <td style={{ padding: '12px' }}>
                                                    <span style={{
                                                        ...getStatusStyle(comm.status),
                                                        padding: '4px 10px',
                                                        borderRadius: '20px',
                                                        fontSize: '11px',
                                                        fontWeight: 'bold',
                                                        display: 'inline-block'
                                                    }}>
                                                        {comm.status}
                                                    </span>
                                                </td>
                                                <td style={{ padding: '12px' }}>
                                                    {comm.status === 'VALIDATED' && (
                                                        <button
                                                            onClick={() => handleMarkAsPaid(comm.id)}
                                                            disabled={processingId === comm.id}
                                                            style={{
                                                                background: '#059669',
                                                                color: 'white',
                                                                border: 'none',
                                                                borderRadius: '5px',
                                                                padding: '6px 12px',
                                                                cursor: processingId === comm.id ? 'not-allowed' : 'pointer',
                                                                fontSize: '12px',
                                                                fontWeight: 'bold',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: '5px'
                                                            }}
                                                        >
                                                            💰 {processingId === comm.id ? 'Traitement...' : 'Marquer comme payé'}
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </Box>
            </div>
        </SiteLayout>
    );
};

export default AdminCommissionsScreen;
